/**
 * "Testdaten bereitstellen": asks before example rows of the automation are
 * copied for the workflow builder. A picture says what happens — example rows
 * of the input reach the workflow's steps as previews, the output is not
 * written — and two lines add what a picture cannot: who sees the copy and
 * when it is removed.
 */
import { ConfirmModal } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { PlayIcon, TableIcon } from '@blocksuite/icons/rc';
import type { FC, ReactNode } from 'react';

import * as styles from './test-data-dialog.css';

const Edge: FC<{ blocked?: boolean }> = ({ blocked }) => (
  <svg
    className={styles.edge}
    viewBox="0 0 36 76"
    focusable="false"
    aria-hidden="true"
    data-role="test-data-edge"
    data-blocked={blocked || undefined}
  >
    {blocked ? (
      <>
        <path className={styles.edgeShaft} d="M4 38H32" />
        <path className={styles.edgeStop} d="M13 33L23 43M23 33L13 43" />
      </>
    ) : (
      <>
        <path className={styles.edgeShaft} d="M4 38H24" />
        <path className={styles.edgeHead} d="M23 31L33 38L23 45Z" />
      </>
    )}
  </svg>
);

const Stage: FC<{
  part: 'input' | 'workflow' | 'output';
  icon: ReactNode;
  name: string;
  note: string;
  children: ReactNode;
}> = ({ part, icon, name, note, children }) => (
  <div
    className={styles.stage}
    data-part={part}
    data-untouched={part === 'output' || undefined}
  >
    <div className={styles.tile} aria-hidden="true">
      <span className={styles.tileIcon}>{icon}</span>
      {children}
    </div>
    <span className={styles.name}>{name}</span>
    <span className={styles.note}>{note}</span>
  </div>
);

/** Table rows; `sample` of them are the example rows. */
const Rows: FC<{ count: number; sample?: number }> = ({
  count,
  sample = 0,
}) => (
  <div className={styles.rows}>
    {Array.from({ length: count }, (_, index) => (
      <span
        key={index}
        className={styles.row}
        data-sample={index < sample || undefined}
      />
    ))}
  </div>
);

export const TestDataDialog: FC<{
  open: boolean;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ open, loading, onCancel, onConfirm }) => {
  const t = useI18n();

  return (
    <ConfirmModal
      open={open}
      onOpenChange={next => {
        if (!next && !loading) onCancel();
      }}
      title={t['com.streamlain.streamlain.execution.test.dialog-title']()}
      cancelText={t['com.streamlain.streamlain.common.cancel']()}
      confirmText={
        loading
          ? t['com.streamlain.streamlain.execution.test.creating']()
          : t['com.streamlain.streamlain.execution.test.confirm']()
      }
      onConfirm={onConfirm}
      onCancel={() => {
        if (!loading) onCancel();
      }}
      confirmButtonOptions={{ variant: 'primary', disabled: loading }}
      cancelButtonOptions={{ disabled: loading }}
      autoFocusConfirm={false}
    >
      <div className={styles.body} data-testid="test-data-dialog">
        <figure
          className={styles.flow}
          role="img"
          aria-label={t[
            'com.streamlain.streamlain.execution.test.dialog-body'
          ]()}
        >
          <Stage
            part="input"
            icon={<TableIcon />}
            name={t['com.streamlain.automation.panel.group.input']()}
            note={t['com.streamlain.streamlain.execution.test.visual.input']()}
          >
            <Rows count={3} sample={2} />
          </Stage>
          <Edge />
          <Stage
            part="workflow"
            icon={<PlayIcon />}
            name={t['com.streamlain.automation.panel.group.workflow']()}
            note={t[
              'com.streamlain.streamlain.execution.test.visual.workflow'
            ]()}
          >
            <div className={styles.steps}>
              {[0, 1, 2].map(index => (
                <span key={index} className={styles.step}>
                  <Rows count={2} sample={2} />
                </span>
              ))}
            </div>
          </Stage>
          <Edge blocked />
          <Stage
            part="output"
            icon={<TableIcon />}
            name={t['com.streamlain.automation.panel.group.output']()}
            note={t['com.streamlain.streamlain.execution.test.visual.output']()}
          >
            <Rows count={3} />
          </Stage>
        </figure>
        <ul className={styles.facts}>
          <li>
            {t['com.streamlain.streamlain.execution.test.dialog-provenance']()}
          </li>
          <li>
            {t['com.streamlain.streamlain.execution.test.dialog-retention']()}
          </li>
        </ul>
      </div>
    </ConfirmModal>
  );
};
